import React, { useState, useMemo } from "react";
import { TraceCanvas, TRACE_SHAPES } from "@/components/TraceCanvas";
import { Confetti, playCorrect } from "@/components/Bits";
import { DoneCard, RoundHeader } from "@/components/Cells";
import { LETTERS } from "@/lib/data";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

const SETS = {
  letters: LETTERS.map(l => ({ id: l.key, text: l.letter, say: `Trace the letter ${l.letter}` })),
  numbers: Array.from({ length: 20 }, (_, i) => ({ id: String(i + 1), text: String(i + 1), say: `Trace the number ${i + 1}` })),
  shapes: TRACE_SHAPES.map(s => ({ id: s, shape: s, say: `Trace the ${s}` })),
};

export default function TracingRunner({ modeId, backTo = "/learn/tracing" }) {
  const all = SETS[modeId] || SETS.letters;
  const [start, setStart] = useState(0);
  const items = useMemo(() => all.slice(start, start + 6), [all, start]);
  const [idx, setIdx] = useState(0);
  const [ok, setOk] = useState(false);
  const [done, setDone] = useState(false);
  const item = items[idx];

  const onComplete = () => { setOk(true); recordAnswer("tracing", true); playCorrect("Beautiful!"); };
  const next = () => {
    setOk(false);
    if (idx + 1 >= items.length) { completeActivity("tracing", modeId); setDone(true); speak("Tracing complete! You're an artist!"); }
    else { setIdx(idx + 1); speak(items[idx + 1].say); }
  };
  const again = () => { setStart(s => (s + 6 >= all.length ? 0 : s + 6)); setIdx(0); setOk(false); setDone(false); };

  if (done) return <DoneCard title="Tracing complete!" emoji="✏️" onAgain={again} backTo={backTo} subtitle="Beautiful tracing! Keep practicing." />;

  return (
    <div className="ll-bg min-h-screen pb-32">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <RoundHeader idx={idx} total={items.length} />
        <div className="ll-card p-5 sm:p-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-purple-900">{item.say}</h2>
          <div className="flex gap-1 overflow-x-auto no-scrollbar mt-3 pb-1" data-testid="trace-picker">
            {all.map((it, i) => <button key={it.id} data-testid={`trace-pick-${it.id}`} onClick={() => { setStart(Math.floor(i / 6) * 6); setIdx(i % 6); setOk(false); }} className={`shrink-0 w-8 h-8 rounded-full text-xs font-bold ${it === item ? "bg-purple-600 text-white" : "bg-purple-100 text-purple-600"}`}>{it.text || "●"}</button>)}
          </div>
          <p className="text-purple-600 font-semibold mt-1">Follow the dotted line with your finger or mouse.</p>
          <div className="mt-6">
            <TraceCanvas key={`${modeId}-${item.id}`} item={item} onComplete={onComplete} />
          </div>
          <div className="flex justify-center gap-3 mt-6">
            <button data-testid="trace-next-button" onClick={next} className={`ll-btn ${ok ? "ll-btn-primary ll-pop" : "ll-btn-secondary"}`}>{ok ? "Next →" : "Skip"}</button>
          </div>
        </div>
      </div>
      <Confetti show={ok} />
    </div>
  );
}
